import { Action, buildKit, TICK_RATE, type CharacterManifest, type FighterKit } from "@arena/shared";

/**
 * Maria Antonietta (docs/personaggi/antonietta.md): the queen of Versailles
 * as a spoiled rococo diva, over the top like the rest of the roster, with a
 * powdered wig as tall as she is. A lace parasol for the blows; S1 tosses a
 * brioche that becomes a mine; S2 pulls the wig down to her feet, and under it
 * she takes a fraction of every blow; the Super Slam pulls out the hairpin and
 * spins her like a top (with the wig down too, if she likes).
 */
export const ANTONIETTA: CharacterManifest = {
  id: "antonietta",
  schemaVersion: 2,
  displayName: "Maria Antonietta",
  archetype: "tank",
  description: "Regina di Versailles con una parrucca alta quanto lei: lancia brioche che esplodono al tocco, si cala la parrucca fino ai piedi e incassa quasi tutto. Suprema: si toglie il fermacapelli e gira come una trottola, più veloce, travolgendo chi le capita attorno.",
  look: { build: "heavy", accent: "#e88fb4" },
  stats: { speed: 45, power: 50, resistance: 60, weight: 60, range: 55, slamCharge: 60 },
  moveset: {
    light1: { name: "Ombrellino di pizzo", startup: 4, active: 2, recovery: 9, range: 1.5, arcDeg: 80, knockback: 2.5, lift: 0, hitstun: 13, slamGain: 6, lunge: 2.5, cancelFrom: 6, nextLight: Action.Light2, superCancel: true, specialCancel: true, damage: 6 },
    light2: { name: "Che maleducati!", startup: 5, active: 3, recovery: 10, range: 1.7, arcDeg: 140, knockback: 3, lift: 0, hitstun: 14, slamGain: 6, lunge: 2, cancelFrom: 7, nextLight: Action.Light3, superCancel: true, specialCancel: true, damage: 7 },
    light3: { name: "Colpo di parrucca", startup: 8, active: 3, recovery: 15, range: 1.9, arcDeg: 100, knockback: 9, lift: 4, hitstun: 20, slamGain: 10, lunge: 3, cancelFrom: 255, nextLight: Action.None, superCancel: true, damage: 12 },
    airAttack: { name: "Caduta di stile", startup: 5, active: 4, recovery: 12, range: 1.6, arcDeg: 360, knockback: 5, lift: 2, hitstun: 14, slamGain: 7, lunge: 0, cancelFrom: 12, nextLight: Action.None, superCancel: true, specialCancel: true, damage: 9 },
    dashAttack: { name: "Fuga a Varennes", startup: 4, active: 4, recovery: 12, range: 1.6, arcDeg: 110, knockback: 6, lift: 1.5, hitstun: 16, slamGain: 8, lunge: 5, travel: 7, cancelFrom: 10, nextLight: Action.None, superCancel: true, specialCancel: true, damage: 10 },
    special1: {
      name: "Che mangino brioche!",
      // Tossed high over the heads: it hits nobody in flight, it lands and waits.
      // The numbers are the blast's; its jump is ANTONIETTA_BRIOCHE.jump.
      startup: 9, active: 1, recovery: 12, range: 0, arcDeg: 360, knockback: 12, lift: 10, hitstun: 18, slamGain: 8, lunge: 0,
      cancelFrom: 255, nextLight: Action.None, superCancel: true, cooldown: 3 * TICK_RATE, damage: 14,
      // A slow lob, about half a second: it reads as a toss, and the mine lands with it.
      projectile: { speed: 8, lifetime: 14, radius: 0.35, height: 2.4 },
    },
    special2: {
      name: "Parrucca-fortezza",
      startup: 10, active: 1, recovery: 6, range: 0, arcDeg: 360, knockback: 0, lift: 0, hitstun: 0, slamGain: 0, lunge: 0,
      cancelFrom: 255, nextLight: Action.None, superCancel: true, damage: 0, cooldown: 12 * TICK_RATE,
    },
    superSlam: {
      name: "La Rivoluzione",
      // The range is the whirlwind's: the wind-up ring shows how far it reaches.
      startup: 12, active: 1, recovery: 1, armorTicks: 12, range: 1.8, arcDeg: 360, knockback: 0, lift: 0, hitstun: 0, slamGain: 0, lunge: 0,
      cancelFrom: 255, nextLight: Action.None, superCancel: false, damage: 0,
    },
  },
};

/**
 * The brioche mine: it lands where the toss ends and goes off when an enemy
 * comes within `trigger` m of it, or after `fuse`; the blast reaches `blast` m.
 * Everyone in the blast jumps just as high (`jump.lift`): on the brioche almost
 * in place (`jump.near`), farther away from it the nearer the edge
 * (`jump.far`). The queen in her own blast jumps the same way, unhurt, wig or
 * not, with `ownStun` ticks without control. At most `max` per queen on the
 * floor: a new one sets off the oldest.
 */
export const ANTONIETTA_BRIOCHE = { fuse: 5 * TICK_RATE, trigger: 0.6, blast: 2, max: 3, jump: { lift: 10, near: 1, far: 12 }, ownStun: 10 } as const;

/**
 * The wig pulled down to her feet (S2) for `ticks`: she still walks (slower,
 * `speed` of her pace), jumps and attacks, takes `damageTaken` of the damage,
 * no knockback and a third of the stun.
 */
export const ANTONIETTA_WIG = { ticks: 6 * TICK_RATE, speed: 0.8, damageTaken: 0.3, hitstunTaken: 0.3 } as const;

/**
 * The whirlwind (Super Slam): `ticks` of spinning, faster and steered by the
 * stick; every `every` ticks it strikes everyone within `radius` m.
 */
export const ANTONIETTA_SPIN = { ticks: 5 * TICK_RATE, every: 10, radius: 1.8, damage: 4, knockback: 3.5, lift: 1, hitstun: 10 } as const;

const kit = buildKit(ANTONIETTA);
/** Her kit while the wig is down: the same moves, a slower walk and an armoured response. */
export const ANTONIETTA_WIG_KIT: FighterKit = Object.freeze({
  ...kit,
  form: "wig" as const,
  maxSpeed: kit.maxSpeed * ANTONIETTA_WIG.speed,
  response: Object.freeze({ knockbackTaken: 0, hitstunTaken: ANTONIETTA_WIG.hitstunTaken, damageTaken: ANTONIETTA_WIG.damageTaken }),
});
